import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../shared/useFetchMoreCallback';
import { questionnairesQuery, questionnairesQueryVariables } from '../generated/questionnairesQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { questionnaireFieldsFragment } from '../models/Questionnaire';

export interface QuestionnairesAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    subscriptionId?: string | null | undefined,
    subscriptionDepartmentId?: string | null | undefined,
    isTemplate?: boolean,
    includeAll?: boolean,
    questionnaireType?: string | null | undefined,
    includeDefault?: boolean,
}

/**
 * Get a list of questionnaires from the store.
 * @param id
 * @param options
 */
export function useQuestionnaires(options: QuestionnairesAsyncLoadPageOptions = {}): AsyncLoadPagedResult<questionnairesQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<questionnairesQuery, questionnairesQueryVariables>(
        gql`
        query questionnairesQuery ($offset: Int, $first: Int, $subscriptionId: ID, $subscriptionDepartmentId: ID, $isTemplate: Boolean, $includeAll: Boolean, $questionnaireType: String, $includeDefault: Boolean) {
            items: questionnaires (offset: $offset, first: $first, subscriptionId: $subscriptionId, subscriptionDepartmentId: $subscriptionDepartmentId, isTemplate: $isTemplate, includeAll: $includeAll, questionnaireType: $questionnaireType, includeDefault: $includeDefault) {
                ...questionnaireFields
            }
        }

        ${questionnaireFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                subscriptionId: options.subscriptionId,
                subscriptionDepartmentId: options.subscriptionDepartmentId,
                isTemplate: options.isTemplate,
                includeAll: options.includeAll,
                questionnaireType: options.questionnaireType,
                includeDefault: options.includeDefault,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<questionnairesQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])]
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
